import store from '../../state';
import authService from '../../services/AuthService';

export class AuthModal {
  private container: HTMLElement | null = null;
  private mode: 'signin' | 'register' | 'subscribe' | 'pending' = 'signin';
  private pendingPlan: '1_month' | '6_month' | '12_month' | null = null;
  private isLoading = false;
  private loadingPlan: string | null = null;
  private errorMessage: string | null = null;
  private noticeMessage: string | null = null;
  private keydownHandler: ((e: KeyboardEvent) => void) | null = null;

  constructor() {
    // Listen to store for modal open/close triggers and auto-close pending modal once entitlement is verified
    store.subscribe((state) => {
      if (state.isAuthModalOpen && !this.container) {
        this.open();
      } else if (!state.isAuthModalOpen && this.container) {
        this.close();
      } else if (this.container && this.mode === 'pending' && state.isAuthenticated && state.subscription?.hasAccess) {
        this.close();
      }
    });
  }

  private isStealthMode(): boolean {
    return (typeof __APP_CONFIG__ !== 'undefined' && __APP_CONFIG__?.stealth_mode) ?? true;
  }

  public open(defaultMode: 'signin' | 'register' | 'subscribe' | 'pending' = 'signin', plan?: '1_month' | '6_month' | '12_month'): void {
    const state = store.getState();
    const isStealth = this.isStealthMode();
    const isEntitled = state.isAuthenticated && state.subscription?.hasAccess;

    if (isStealth && state.isAuthenticated && !isEntitled) {
      this.mode = 'pending';
      // Automatically verify activation status in the background so entitled users never need to click manually
      authService.refreshSessionCookie().then((ok) => {
        if (ok && store.getState().subscription?.hasAccess && this.container && this.mode === 'pending') {
          this.close();
        }
      }).catch(() => {});
    } else {
      this.mode = defaultMode;
    }

    this.pendingPlan = plan || null;
    this.errorMessage = null;
    this.noticeMessage = null;
    this.isLoading = false;
    this.loadingPlan = null;

    if (this.container) {
      this.close();
    }

    this.container = document.createElement('div');
    this.container.id = 'gw-auth-modal-overlay';
    this.container.className = 'gw-modal-backdrop';
    document.body.appendChild(this.container);

    this.render();

    this.keydownHandler = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !this.isLoading) {
        this.close();
      }
    };
    window.addEventListener('keydown', this.keydownHandler);
  }

  public close(): void {
    if (this.keydownHandler) {
      window.removeEventListener('keydown', this.keydownHandler);
      this.keydownHandler = null;
    }

    if (this.container && this.container.parentNode) {
      this.container.parentNode.removeChild(this.container);
      this.container = null;
    }

    if (store.getState().isAuthModalOpen) {
      store.update({ isAuthModalOpen: false });
    }
  }

  private render(): void {
    if (!this.container) return;

    if (this.mode === 'pending') {
      this.renderPendingView();
    } else if (this.mode === 'subscribe') {
      this.renderSubscribeView();
    } else {
      this.renderAuthView();
    }

    this.bindEvents();
  }

  private renderPendingView(): void {
    if (!this.container) return;
    const state = store.getState();
    const userEmail = state.user?.email || 'Registered User';
    const userUid = state.user?.uid || '';

    this.container.innerHTML = `
      <div class="gw-auth-dialog gw-pending-dialog" role="dialog" aria-modal="true" aria-labelledby="gw-pending-title">
        <button id="gw-auth-close-btn" class="gw-auth-close" title="Close" aria-label="Close">&times;</button>
        
        <div class="gw-auth-header">
          <div class="gw-auth-logo-badge" style="background: rgba(245, 158, 11, 0.2); border-color: rgba(245, 158, 11, 0.4);">
            <span class="gw-auth-logo-icon">⏳</span>
          </div>
          <h2 id="gw-pending-title" class="gw-auth-title">
            Account Pending Activation
          </h2>
          <p class="gw-auth-subtitle">
            Welcome to SkySkout! We are currently operating in an invite-only stealth preview.
          </p>
        </div>

        ${this.noticeMessage ? `
          <div class="gw-auth-notice-banner">
            <span>ℹ️ ${this.escapeHtml(this.noticeMessage)}</span>
          </div>
        ` : ''}

        <div class="gw-auth-pending-card">
          <div class="gw-auth-pending-info">
            <div class="gw-auth-info-row">
              <span class="gw-auth-info-label">Signed in as:</span>
              <span class="gw-auth-info-val">${this.escapeHtml(userEmail)}</span>
            </div>
            <div class="gw-auth-info-row">
              <span class="gw-auth-info-label">Status:</span>
              <span class="gw-auth-status-pill pending">Awaiting Administrator Entitlement</span>
            </div>
          </div>

          <div class="gw-auth-uid-box">
            <span class="gw-auth-uid-label">Your Firebase User ID (UID):</span>
            <div class="gw-auth-uid-row">
              <code id="gw-auth-uid-val" class="gw-auth-uid-code">${this.escapeHtml(userUid)}</code>
              <button id="gw-auth-copy-uid-btn" class="gw-auth-copy-btn" title="Copy UID to clipboard">📋 Copy UID</button>
            </div>
            <p class="gw-auth-uid-hint">
              Share this User ID with your administrator to enable access to the forecast layer catalog.
            </p>
          </div>
        </div>

        <div class="gw-auth-actions-row">
          <button id="gw-auth-refresh-btn" class="gw-auth-submit-btn">
            <span>🔄 Check Activation Status</span>
          </button>
          <button id="gw-auth-signout-btn" class="gw-auth-link-btn" style="color: #94a3b8; font-size: 0.85rem; padding: 6px; background: none; border: none; cursor: pointer;">
            Sign Out
          </button>
        </div>
      </div>
    `;
  }

  private renderSubscribeView(): void {
    if (!this.container) return;

    const isAuth = store.getState().isAuthenticated;

    this.container.innerHTML = `
      <div class="gw-auth-dialog gw-subscribe-dialog" role="dialog" aria-modal="true" aria-labelledby="gw-sub-title">
        <button id="gw-auth-close-btn" class="gw-auth-close" title="Close" aria-label="Close">&times;</button>
        
        <div class="gw-auth-header">
          <div class="gw-auth-logo-badge">
            <span class="gw-auth-logo-icon">🌤️</span>
          </div>
          <h2 id="gw-sub-title" class="gw-auth-title">
            SkySkout Pro Membership
          </h2>
          <p class="gw-auth-subtitle">
            All plans include a <strong>14-day free trial</strong> with full access to high-res forecast models, live satellite, radar loops & dark skies.
          </p>
        </div>

        <!-- Mode Toggle Pill -->
        <div class="gw-auth-toggle-pill">
          ${!isAuth ? `
            <button id="gw-auth-tab-signin" class="gw-auth-tab-btn">Sign In</button>
            <button id="gw-auth-tab-register" class="gw-auth-tab-btn">Register</button>
          ` : ''}
          ${!this.isStealthMode() ? '<button id="gw-auth-tab-subscribe" class="gw-auth-tab-btn active">✨ Pro Plans</button>' : ''}
        </div>

        ${this.noticeMessage ? `
          <div class="gw-auth-notice-banner">
            <span>ℹ️ ${this.escapeHtml(this.noticeMessage)}</span>
          </div>
        ` : ''}

        ${this.errorMessage ? `
          <div class="gw-auth-error-banner" role="alert">
            <span class="gw-auth-error-icon">⚠️</span>
            <span>${this.escapeHtml(this.errorMessage)}</span>
          </div>
        ` : ''}

        <!-- Pricing Cards Grid -->
        <div class="gw-pricing-grid">
          <!-- 1 Month Card -->
          <div class="gw-pricing-card">
            <div class="gw-pricing-card-header">
              <span class="gw-pricing-plan-name">Monthly</span>
              <div class="gw-pricing-cost">
                <span class="gw-pricing-amount">$4.99</span>
                <span class="gw-pricing-cadence">/ month</span>
              </div>
              <span class="gw-pricing-trial-tag">14-Day Free Trial</span>
            </div>
            <p class="gw-pricing-subtext">Billed monthly after trial. Cancel anytime.</p>
            <ul class="gw-pricing-features">
              <li>✓ All high-res weather forecast layers</li>
              <li>✓ Live GOES satellite & MRMS radar loops</li>
              <li>✓ Dark skies, optical seeing & aurora Kp</li>
              <li>✓ Mountain temps, snowpack & wind vectors</li>
            </ul>
            <button id="gw-plan-btn-1mo" class="gw-pricing-btn ${this.loadingPlan === '1_month' ? 'loading' : ''}" ${this.isLoading ? 'disabled' : ''}>
              ${this.loadingPlan === '1_month' ? '<span class="gw-auth-spinner"></span> Redirecting...' : 'Start 14-Day Free Trial'}
            </button>
          </div>

          <!-- 6 Month Card (Highlighted / Most Popular) -->
          <div class="gw-pricing-card gw-pricing-card-featured">
            <div class="gw-pricing-ribbon">Most Popular</div>
            <div class="gw-pricing-card-header">
              <span class="gw-pricing-plan-name">Seasonal Active Pass</span>
              <div class="gw-pricing-cost">
                <span class="gw-pricing-amount">$24.99</span>
                <span class="gw-pricing-cadence">/ 6 months</span>
              </div>
              <span class="gw-pricing-trial-tag">14-Day Free Trial • Save 16%</span>
            </div>
            <p class="gw-pricing-subtext">Covers your entire hiking, climbing, or ski season.</p>
            <ul class="gw-pricing-features">
              <li>✓ <strong>Everything in Monthly</strong></li>
              <li>✓ Optimal for peak seasonal outdoor adventures</li>
              <li>✓ High-resolution HRRR/ECMWF blends</li>
              <li>✓ Alpenglow & sunset color predictions</li>
            </ul>
            <button id="gw-plan-btn-6mo" class="gw-pricing-btn gw-pricing-btn-highlight ${this.loadingPlan === '6_month' ? 'loading' : ''}" ${this.isLoading ? 'disabled' : ''}>
              ${this.loadingPlan === '6_month' ? '<span class="gw-auth-spinner"></span> Redirecting...' : 'Start 14-Day Free Trial'}
            </button>
          </div>

          <!-- 12 Month Card (Best Value) -->
          <div class="gw-pricing-card">
            <div class="gw-pricing-badge-value">Best Value</div>
            <div class="gw-pricing-card-header">
              <span class="gw-pricing-plan-name">Annual Pro</span>
              <div class="gw-pricing-cost">
                <span class="gw-pricing-amount">$39.99</span>
                <span class="gw-pricing-cadence">/ year</span>
              </div>
              <span class="gw-pricing-trial-tag">14-Day Free Trial • Save 33%</span>
            </div>
            <p class="gw-pricing-subtext">Equivalent to just $3.33/mo for year-round intelligence.</p>
            <ul class="gw-pricing-features">
              <li>✓ <strong>Full year-round coverage</strong></li>
              <li>✓ All summer, winter & shoulder-season models</li>
              <li>✓ Priority atmospheric model compute</li>
              <li>✓ Save 33% compared to monthly</li>
            </ul>
            <button id="gw-plan-btn-12mo" class="gw-pricing-btn ${this.loadingPlan === '12_month' ? 'loading' : ''}" ${this.isLoading ? 'disabled' : ''}>
              ${this.loadingPlan === '12_month' ? '<span class="gw-auth-spinner"></span> Redirecting...' : 'Start 14-Day Free Trial'}
            </button>
          </div>
        </div>

        <div class="gw-auth-footer" style="margin-top: 18px;">
          <p class="gw-auth-footer-note">
            🔒 Secure checkout powered by Stripe. You will not be charged until your 14-day trial ends. Base maps remain 100% free forever.
          </p>
        </div>
      </div>
    `;
  }

  private renderAuthView(): void {
    if (!this.container) return;

    const isRegister = this.mode === 'register';

    this.container.innerHTML = `
      <div class="gw-auth-dialog" role="dialog" aria-modal="true" aria-labelledby="gw-auth-title">
        <button id="gw-auth-close-btn" class="gw-auth-close" title="Close" aria-label="Close">&times;</button>
        
        <div class="gw-auth-header">
          <div class="gw-auth-logo-badge">
            <span class="gw-auth-logo-icon">🌤️</span>
          </div>
          <h2 id="gw-auth-title" class="gw-auth-title">
            ${isRegister ? 'Create Your SkySkout Account' : 'Welcome to SkySkout'}
          </h2>
          <p class="gw-auth-subtitle">
            ${isRegister ? 'Register to sync saved locations, atmospheric alerts, and start your 14-day free trial.' : 'Sign in to access high-resolution forecasts & live weather layers.'}
          </p>
        </div>

        <!-- Mode Toggle Pill -->
        <div class="gw-auth-toggle-pill">
          <button id="gw-auth-tab-signin" class="gw-auth-tab-btn ${!isRegister ? 'active' : ''}">Sign In</button>
          <button id="gw-auth-tab-register" class="gw-auth-tab-btn ${isRegister ? 'active' : ''}">Register</button>
          ${!this.isStealthMode() ? '<button id="gw-auth-tab-subscribe" class="gw-auth-tab-btn">✨ Pro Plans</button>' : ''}
        </div>

        ${this.noticeMessage ? `
          <div class="gw-auth-notice-banner">
            <span>ℹ️ ${this.escapeHtml(this.noticeMessage)}</span>
          </div>
        ` : ''}

        ${this.errorMessage ? `
          <div class="gw-auth-error-banner" role="alert">
            <span class="gw-auth-error-icon">⚠️</span>
            <span>${this.escapeHtml(this.errorMessage)}</span>
          </div>
        ` : ''}

        <!-- Social Logins -->
        <div class="gw-auth-social-group">
          <button id="gw-auth-google-btn" class="gw-auth-btn-social" ${this.isLoading ? 'disabled' : ''}>
            <svg class="gw-auth-icon-svg" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.06H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.94l2.85-2.22.81-.63z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84c.87-2.6 3.3-4.52 6.16-4.52z"/>
            </svg>
            <span>Continue with Google</span>
          </button>
          
          <button id="gw-auth-apple-btn" class="gw-auth-btn-social" ${this.isLoading ? 'disabled' : ''}>
            <svg class="gw-auth-icon-svg" viewBox="0 0 24 24" fill="currentColor">
              <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M15.97 6.37c.65-.79 1.09-1.89.97-2.99-.95.04-2.1.64-2.77 1.43-.59.69-1.12 1.81-.98 2.9.01 0 .02 0 .04 0 1.05 0 2.15-.62 2.74-1.34z"/>
            </svg>
            <span>Continue with Apple</span>
          </button>
        </div>

        <div class="gw-auth-divider">
          <span>or with email</span>
        </div>

        <!-- Form -->
        <form id="gw-auth-form" class="gw-auth-form">
          <div class="gw-auth-input-group">
            <label for="gw-auth-email" class="gw-auth-label">Email Address</label>
            <input
              type="email"
              id="gw-auth-email"
              class="gw-auth-input"
              placeholder="you@example.com"
              required
              autocomplete="email"
            />
          </div>

          <div class="gw-auth-input-group">
            <div class="gw-auth-label-row">
              <label for="gw-auth-password" class="gw-auth-label">Password</label>
              ${!isRegister ? `
                <a href="https://skyskout-ec4b1.firebaseapp.com/__/auth/action" target="_blank" class="gw-auth-forgot" rel="noopener">Forgot password?</a>
              ` : ''}
            </div>
            <input
              type="password"
              id="gw-auth-password"
              class="gw-auth-input"
              placeholder="${isRegister ? 'At least 6 characters' : 'Enter password'}"
              required
              autocomplete="${isRegister ? 'new-password' : 'current-password'}"
              minlength="6"
            />
          </div>

          <button
            type="submit"
            id="gw-auth-submit-btn"
            class="gw-auth-submit-btn ${this.isLoading ? 'loading' : ''}"
            ${this.isLoading ? 'disabled' : ''}
          >
            ${this.isLoading ? `
              <span class="gw-auth-spinner"></span>
              <span>${isRegister ? 'Creating Account...' : 'Signing In...'}</span>
            ` : `
              <span>${isRegister ? 'Create Account' : 'Sign In'}</span>
            `}
          </button>
        </form>

        <div class="gw-auth-footer">
          ${isRegister ? `
            <p>Already have an account? <a href="#" id="gw-auth-link-signin" class="gw-auth-link">Sign In</a></p>
          ` : `
            <p>Don't have an account? <a href="#" id="gw-auth-link-register" class="gw-auth-link">Create one for free</a></p>
          `}
          ${!this.isStealthMode() ? `
          <p style="margin-top: 8px;">
            <a href="#" id="gw-auth-link-subscribe" class="gw-auth-link" style="color: #60a5fa;">✨ Explore SkySkout Pro Plans (14-Day Trial)</a>
          </p>
          ` : ''}
        </div>
      </div>
    `;
  }

  private bindEvents(): void {
    if (!this.container) return;

    // Close button
    const closeBtn = this.container.querySelector('#gw-auth-close-btn') as HTMLElement;
    if (closeBtn) closeBtn.onclick = () => this.close();

    // Click outside backdrop to close
    this.container.onclick = (e) => {
      if (e.target === this.container && !this.isLoading) {
        this.close();
      }
    };

    // Pending mode event bindings
    if (this.mode === 'pending') {
      const copyBtn = this.container.querySelector('#gw-auth-copy-uid-btn') as HTMLElement;
      const state = store.getState();
      const uid = state.user?.uid || '';
      if (copyBtn && uid) {
        copyBtn.onclick = () => {
          navigator.clipboard.writeText(uid).then(() => {
            copyBtn.innerText = '✓ Copied!';
            setTimeout(() => { copyBtn.innerText = '📋 Copy UID'; }, 2000);
          }).catch(() => {});
        };
      }

      const refreshBtn = this.container.querySelector('#gw-auth-refresh-btn') as HTMLElement;
      if (refreshBtn) {
        refreshBtn.onclick = async () => {
          refreshBtn.innerText = 'Checking...';
          const ok = await authService.refreshSessionCookie();
          const currentSub = store.getState().subscription;
          if (ok && currentSub?.hasAccess) {
            this.close();
          } else {
            refreshBtn.innerText = 'Still Pending Activation';
            setTimeout(() => { refreshBtn.innerHTML = '<span>🔄 Check Activation Status</span>'; }, 2000);
          }
        };
      }

      const signoutBtn = this.container.querySelector('#gw-auth-signout-btn') as HTMLElement;
      if (signoutBtn) {
        signoutBtn.onclick = async () => {
          await authService.signOut();
          this.close();
        };
      }
      return;
    }

    // Tab buttons
    const signinTab = this.container.querySelector('#gw-auth-tab-signin') as HTMLElement;
    const registerTab = this.container.querySelector('#gw-auth-tab-register') as HTMLElement;
    const subTab = this.container.querySelector('#gw-auth-tab-subscribe') as HTMLElement;

    if (signinTab) signinTab.onclick = () => { this.mode = 'signin'; this.errorMessage = null; this.render(); };
    if (registerTab) registerTab.onclick = () => { this.mode = 'register'; this.errorMessage = null; this.render(); };
    if (subTab) subTab.onclick = () => { this.mode = 'subscribe'; this.errorMessage = null; this.render(); };

    // Footer links
    const signinLink = this.container.querySelector('#gw-auth-link-signin') as HTMLElement;
    const registerLink = this.container.querySelector('#gw-auth-link-register') as HTMLElement;
    const subLink = this.container.querySelector('#gw-auth-link-subscribe') as HTMLElement;

    if (signinLink) signinLink.onclick = (e) => { e.preventDefault(); this.mode = 'signin'; this.errorMessage = null; this.render(); };
    if (registerLink) registerLink.onclick = (e) => { e.preventDefault(); this.mode = 'register'; this.errorMessage = null; this.render(); };
    if (subLink) subLink.onclick = (e) => { e.preventDefault(); this.mode = 'subscribe'; this.errorMessage = null; this.render(); };

    // Plan Buttons in Subscribe Mode
    if (this.mode === 'subscribe') {
      const handlePlanClick = async (plan: '1_month' | '6_month' | '12_month') => {
        const isAuth = store.getState().isAuthenticated;
        if (!isAuth) {
          this.pendingPlan = plan;
          this.mode = 'register';
          this.noticeMessage = `Please create an account to activate your 14-day free trial on the ${plan.replace('_', ' ')} plan.`;
          this.render();
          return;
        }

        this.setLoading(true);
        this.loadingPlan = plan;
        this.render();

        const result = await authService.createCheckoutSession(plan);
        if (!result.success) {
          this.errorMessage = result.error || 'Failed to redirect to Stripe checkout.';
          this.setLoading(false);
          this.loadingPlan = null;
          this.render();
        }
      };

      const btn1mo = this.container.querySelector('#gw-plan-btn-1mo') as HTMLElement;
      const btn6mo = this.container.querySelector('#gw-plan-btn-6mo') as HTMLElement;
      const btn12mo = this.container.querySelector('#gw-plan-btn-12mo') as HTMLElement;

      if (btn1mo) btn1mo.onclick = () => handlePlanClick('1_month');
      if (btn6mo) btn6mo.onclick = () => handlePlanClick('6_month');
      if (btn12mo) btn12mo.onclick = () => handlePlanClick('12_month');
      return;
    }

    // Google Sign-In
    const googleBtn = this.container.querySelector('#gw-auth-google-btn') as HTMLElement;
    if (googleBtn) {
      googleBtn.onclick = async () => {
        this.setLoading(true);
        try {
          await authService.signInWithGoogle();
          await this.afterAuthSuccess();
        } catch (err: any) {
          console.error('Google Sign In error:', err);
          this.errorMessage = this.formatFirebaseError(err);
          this.setLoading(false);
          this.render();
        }
      };
    }

    // Apple Sign-In
    const appleBtn = this.container.querySelector('#gw-auth-apple-btn') as HTMLElement;
    if (appleBtn) {
      appleBtn.onclick = async () => {
        this.setLoading(true);
        try {
          await authService.signInWithApple();
          await this.afterAuthSuccess();
        } catch (err: any) {
          console.error('Apple Sign In error:', err);
          this.errorMessage = this.formatFirebaseError(err);
          this.setLoading(false);
          this.render();
        }
      };
    }

    // Email/Password Form
    const form = this.container.querySelector('#gw-auth-form') as HTMLFormElement;
    if (form) {
      form.onsubmit = async (e) => {
        e.preventDefault();
        const emailInput = this.container?.querySelector('#gw-auth-email') as HTMLInputElement;
        const passInput = this.container?.querySelector('#gw-auth-password') as HTMLInputElement;
        if (!emailInput || !passInput) return;

        const email = emailInput.value.trim();
        const password = passInput.value;

        if (!email || !password) {
          this.errorMessage = 'Please enter both email and password.';
          this.render();
          return;
        }

        this.setLoading(true);
        try {
          if (this.mode === 'register') {
            await authService.registerWithEmail(email, password);
          } else {
            await authService.signInWithEmail(email, password);
          }
          await this.afterAuthSuccess();
        } catch (err: any) {
          console.error('Auth error:', err);
          this.errorMessage = this.formatFirebaseError(err);
          this.setLoading(false);
          this.render();
        }
      };
    }
  }

  private async afterAuthSuccess(): Promise<void> {
    if (!store.getState().subscription?.hasAccess) {
      await authService.waitForSessionRefresh();
    }
    const isStealth = this.isStealthMode();
    const sub = store.getState().subscription;
    const isEntitled = sub && sub.hasAccess;

    if (isStealth && !isEntitled) {
      this.mode = 'pending';
      this.setLoading(false);
      this.render();
      return;
    }

    // If user clicked a plan before registering/signing in, direct them to checkout
    if (this.pendingPlan) {
      const plan = this.pendingPlan;
      this.pendingPlan = null;
      this.mode = 'subscribe';
      this.loadingPlan = plan;
      this.render();
      const res = await authService.createCheckoutSession(plan);
      if (!res.success) {
        this.errorMessage = res.error || 'Failed to start Stripe checkout session.';
        this.setLoading(false);
        this.loadingPlan = null;
        this.render();
      }
      return;
    }

    // Check if user already has access or trial
    if (!sub || !sub.hasAccess) {
      // Offer subscription selection
      this.mode = 'subscribe';
      this.setLoading(false);
      this.render();
    } else {
      this.close();
    }
  }

  private setLoading(loading: boolean): void {
    this.isLoading = loading;
  }

  private formatFirebaseError(err: any): string {
    const code = err.code || '';
    switch (code) {
      case 'auth/invalid-email':
        return 'Please provide a valid email address.';
      case 'auth/user-disabled':
        return 'This account has been disabled. Please contact support.';
      case 'auth/user-not-found':
      case 'auth/wrong-password':
      case 'auth/invalid-credential':
        return 'Incorrect email or password. Please verify your credentials.';
      case 'auth/email-already-in-use':
        return 'An account with this email address already exists. Try signing in.';
      case 'auth/weak-password':
        return 'Password should be at least 6 characters.';
      case 'auth/popup-closed-by-user':
        return 'Sign-in window was closed before completing.';
      default:
        return err.message || 'An unexpected error occurred during authentication.';
    }
  }

  private escapeHtml(str: string): string {
    return str.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }
}

export default AuthModal;
